// Part 1 — sequential setup steps with a LOCKED Next.
//
// A docked, non-blocking command card (never a modal wall) that shows ONE
// step at a time in checklist order from GET /api/onboarding/status: must-have
// steps first, then recommended. Next stays disabled until the endpoint reports
// that step `done`; optional steps also offer "Skip for now". A CTA deep-links
// via the app's tab navigation. "Finish later" minimizes to a Resume pill
// (rendered by the parent). "See all steps" expands the full grouped checklist.
//
// Styled in the HQ command language — smoked-glass panel, amber accent
// lighting, engineered top-edge + corner brackets, serif department titles,
// mono status. Matches the cockpit rooms so onboarding feels like the product,
// not a light-themed popover bolted on.

import React from 'react';
import hq from '../../hq/theme';
import {
  orderSetupSteps, isMustHave, isOptionalStep, isStepDone, canAdvanceSetup,
  setupComplete, setupProgress,
} from './tourLogic';

const AMBER = hq.color.amber;

function RefreshIcon({ spinning }) {
  return (
    <span
      aria-hidden
      style={{ display: 'inline-block', transition: 'transform 0.6s', transform: spinning ? 'rotate(360deg)' : 'none' }}
    >↻</span>
  );
}

// L-shaped corner brackets — the "engineered instrument" tell.
function Corner({ pos }) {
  const base = { position: 'absolute', width: 12, height: 12, borderColor: hq.color.lineActive, pointerEvents: 'none', opacity: 0.7 };
  const map = {
    tl: { top: 8, left: 8, borderTop: `1px solid`, borderLeft: `1px solid` },
    tr: { top: 8, right: 8, borderTop: `1px solid`, borderRight: `1px solid` },
    bl: { bottom: 8, left: 8, borderBottom: `1px solid`, borderLeft: `1px solid` },
    br: { bottom: 8, right: 8, borderBottom: `1px solid`, borderRight: `1px solid` },
  };
  return <span aria-hidden style={{ ...base, ...map[pos] }} />;
}

function GroupedChecklist({ orderedSteps }) {
  const must = orderedSteps.filter(isMustHave);
  const rec = orderedSteps.filter(isOptionalStep);
  const Label = (t) => (
    <div style={{ fontFamily: hq.type.mono, fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: hq.color.textMuted, marginTop: 8 }}>{t}</div>
  );
  const Row = (s) => (
    <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0' }}>
      <span style={{
        marginTop: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: 999,
        fontSize: 9, fontWeight: 700,
        background: isStepDone(s) ? hq.color.ok : 'rgba(255,255,255,0.05)',
        color: isStepDone(s) ? '#0b0f0c' : hq.color.textMuted,
        border: isStepDone(s) ? 'none' : `1px solid ${hq.color.line}`,
      }}>{isStepDone(s) ? '✓' : ''}</span>
      <div style={{ minWidth: 0, fontSize: 12, fontWeight: 600, color: isStepDone(s) ? hq.color.textMuted : hq.color.textSec, textDecoration: isStepDone(s) ? 'line-through' : 'none' }}>{s.label}</div>
    </div>
  );
  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${hq.color.line}`, paddingTop: 6 }}>
      {must.length > 0 && (<>{Label('Must have')}{must.map(Row)}</>)}
      {rec.length > 0 && (<>{Label('Recommended')}{rec.map(Row)}</>)}
    </div>
  );
}

export default function SetupSteps({
  status,
  tourState,
  currentIndex,
  onCta,
  onNext,
  onSkipStep,
  onFinishLater,
  onStartReveals,
  onRefresh,
  refreshing,
}) {
  const [expanded, setExpanded] = React.useState(false);
  const orderedSteps = React.useMemo(() => orderSetupSteps(status && status.steps), [status]);
  const skipped = (tourState && tourState.skipped) || {};
  const { done, total } = setupProgress(orderedSteps, skipped);
  const complete = setupComplete(orderedSteps, skipped);

  const idx = Math.max(0, Math.min(currentIndex, orderedSteps.length - 1));
  const step = orderedSteps[idx];
  const showStartTour = complete;
  const canAdvance = step ? canAdvanceSetup(step, skipped) : false;

  const ghostBtn = {
    background: 'transparent', border: 'none', cursor: 'pointer',
    fontFamily: hq.type.mono, fontSize: 10.5, letterSpacing: '0.04em',
    color: hq.color.textMuted,
  };

  return (
    <div
      className="fixed z-40"
      style={{
        bottom: 20, left: 250, width: 380, maxWidth: 'calc(100vw - 1.5rem)',
        background: hq.color.glass,
        border: `1px solid ${hq.color.line}`,
        borderRadius: 14,
        backdropFilter: 'blur(14px)',
        boxShadow: `${hq.surface.shadow}, ${hq.surface.glow(AMBER)}`,
        color: hq.color.text,
        overflow: 'hidden',
        position: 'fixed',
      }}
      role="dialog"
      aria-label="Set up your workspace"
    >
      {/* engineered top edge */}
      <div aria-hidden style={{ height: 1.5, background: hq.surface.topEdge, opacity: 0.9 }} />
      <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: `1px solid ${hq.color.line}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 8,
            background: `linear-gradient(160deg, ${hq.color.bright}, ${AMBER})`, color: '#0b0a05',
            fontFamily: hq.type.mono, fontSize: 12.5, fontWeight: 800,
            boxShadow: hq.surface.glow(AMBER),
          }}>6</span>
          <div style={{ fontFamily: hq.type.serif, fontSize: 15, fontWeight: 600, color: hq.color.text, letterSpacing: '-0.01em' }}>Set up your workspace</div>
          <span style={{ fontFamily: hq.type.mono, fontSize: 10.5, color: AMBER, background: 'rgba(245,166,35,0.10)', border: `1px solid ${hq.color.line}`, borderRadius: 999, padding: '1px 7px' }}>{done}/{total}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onRefresh} title="Refresh status" style={{ ...ghostBtn, fontSize: 13, color: hq.color.textSec }}>
            <RefreshIcon spinning={refreshing} />
          </button>
          <button onClick={onFinishLater} style={ghostBtn}
            onMouseEnter={(e) => (e.currentTarget.style.color = hq.color.text)}
            onMouseLeave={(e) => (e.currentTarget.style.color = hq.color.textMuted)}
          >Finish later</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '14px' }}>
        {showStartTour ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: hq.color.ok, fontSize: 14, fontWeight: 700 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 999, background: hq.color.ok, color: '#0b0f0c', fontSize: 11 }}>✓</span>
              You're set up to operate
            </div>
            <p style={{ fontSize: 12.5, color: hq.color.textSec, marginTop: 6, lineHeight: 1.5 }}>The must-haves are done. Take a 2-minute tour of what the platform does for you.</p>
            <button onClick={onStartReveals} style={{
              width: '100%', marginTop: 12, padding: '11px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: `linear-gradient(180deg, ${hq.color.bright}, ${AMBER})`, color: '#0b0a05',
              fontFamily: hq.type.body, fontWeight: 800, fontSize: 13.5,
              boxShadow: hq.surface.glow(AMBER),
            }}>Take the 2-minute tour →</button>
          </div>
        ) : step ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ fontFamily: hq.type.mono, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: isMustHave(step) ? AMBER : hq.color.textMuted, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span aria-hidden style={{ color: isMustHave(step) ? AMBER : hq.color.textMuted }}>◆</span>
                {isMustHave(step) ? 'Must have' : 'Recommended'}
              </span>
              {isStepDone(step) && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: hq.type.mono, fontSize: 9.5, fontWeight: 700, color: hq.color.ok }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: 999, background: hq.color.ok, color: '#0b0f0c' }}>✓</span> Done
                </span>
              )}
            </div>
            <div style={{ fontFamily: hq.type.serif, fontSize: 17, fontWeight: 600, color: hq.color.text, marginTop: 6, letterSpacing: '-0.01em' }}>{step.label}</div>
            {step.why && <p style={{ fontSize: 12.5, color: hq.color.textSec, marginTop: 5, lineHeight: 1.5 }}>{step.why}</p>}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
              {step.cta_label && (
                <button
                  onClick={() => onCta(step)}
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                    background: 'rgba(245,166,35,0.10)', border: `1px solid ${hq.color.lineActive}`,
                    color: AMBER, fontFamily: hq.type.body, fontWeight: 700, fontSize: 12.5,
                    transition: 'box-shadow 140ms ease, background 140ms ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = hq.surface.glow(AMBER); e.currentTarget.style.background = 'rgba(245,166,35,0.16)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.background = 'rgba(245,166,35,0.10)'; }}
                >{step.cta_label}</button>
              )}
              <button
                onClick={onNext}
                disabled={!canAdvance}
                title={canAdvance ? 'Next' : 'Finish this step to continue'}
                style={{
                  padding: '10px 16px', borderRadius: 10, fontFamily: hq.type.body, fontWeight: 800, fontSize: 12.5,
                  cursor: canAdvance ? 'pointer' : 'not-allowed',
                  border: canAdvance ? 'none' : `1px solid ${hq.color.line}`,
                  background: canAdvance ? `linear-gradient(180deg, ${hq.color.bright}, ${AMBER})` : 'rgba(255,255,255,0.03)',
                  color: canAdvance ? '#0b0a05' : hq.color.textMuted,
                  boxShadow: canAdvance ? hq.surface.glow(AMBER) : 'none',
                }}
              >{canAdvance ? 'Next →' : 'Next 🔒'}</button>
            </div>

            {isOptionalStep(step) && !isStepDone(step) && (
              <button onClick={() => onSkipStep(step)} style={{ ...ghostBtn, marginTop: 10 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = hq.color.textSec)}
                onMouseLeave={(e) => (e.currentTarget.style.color = hq.color.textMuted)}
              >Skip for now</button>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: hq.color.textMuted, padding: '8px 0', fontFamily: hq.type.mono }}>Loading your setup checklist…</div>
        )}

        {/* See all steps */}
        <button
          onClick={() => setExpanded(v => !v)}
          style={{ ...ghostBtn, color: AMBER, marginTop: 13, fontWeight: 700 }}
          onMouseEnter={(e) => (e.currentTarget.style.color = hq.color.bright)}
          onMouseLeave={(e) => (e.currentTarget.style.color = AMBER)}
        >
          {expanded ? 'Hide all steps' : `See all steps (${done}/${total})`}
        </button>
        {expanded && <GroupedChecklist orderedSteps={orderedSteps} />}
      </div>
    </div>
  );
}
